import {inject, Injectable} from './di';
import {Logger} from './logger';
import {ConfigService} from './config/config-service';
import {NetworkInfoService} from './network-info-service';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {RoomNumberService} from './room-number-service';
import {AppInitializeService} from './app-initialize-service';
import {StorageService} from './storage-service';
import {TimeService} from './time-service';
import {HotelInfoService} from './hotel-info-service';
import {LoggerConfig} from './logger/logger-config';
import {deepClone} from './utils/deep-clone';
import {LogLevel} from './logger/log-level';
import {XhrAppenderConfig} from './logger/appenders/xhr/xhr-appender-config';
import {SentryBreadcrumbsAppenderConfig} from './logger/appenders/sentry-breadcrumbs/sentry-breadcrumbs-appender-config';
import {LocalStorageAppenderConfig} from './logger/appenders/local-storage/local-storage-appender-config';
import {simplifyMacAddress} from './utils/simplify-mac-address';

/**
 * The time (in ms since Unix Epoch) that the remote debugging session ended.
 */
const REMOTE_DEBUG_SESSION_END_LOCAL_STORAGE_KEY = 'remote_debug';

const DEFAULT_DEBUG_SESSION_DURATION = 6 * 60 * 60 * 1000; // 6 hours in ms

/**
 * Remote debug service. This service is responsible for all the functionality of remote debugging of the application.
 * Remote debugging is possible using two tools - Chili and Weinre. Information about remote debugging can be found
 * in the project documentation.
 */
@Injectable()
export class RemoteDebugService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly configService = inject(ConfigService);
  private readonly networkInfoService = inject(NetworkInfoService);
  private readonly roomNumberService = inject(RoomNumberService);
  private readonly storageService = inject(StorageService);
  private readonly timeService = inject(TimeService);
  private readonly hotelInfoService = inject(HotelInfoService);

  private isRemoteDebugActive$ = new BehaviorSubject<boolean>(false);
  private preservedLoggerConfig: LoggerConfig | undefined = undefined;

  constructor() {
    combineLatest([
      this.configService.config$,
      this.networkInfoService.networkInfo$,
      this.roomNumberService.roomNumber$,
    ]).subscribe(([config, networkInfo, roomNumber]) => {
      if (this.isRemoteDebugActive$.getValue()) {
        return;
      }

      const macs = (config?.remoteDebug?.turningOnFor?.macAddresses || [])
        .filter((e) => e && typeof e === 'string')
        .map((e) => simplifyMacAddress(e));
      const ips = (config?.remoteDebug?.turningOnFor?.ips || [])
        .filter((e) => e && typeof e === 'string')
        .map((e) => e.trim().toLowerCase());
      const rooms = (config?.remoteDebug?.turningOnFor?.rooms || [])
        .filter((e) => e != null && (typeof e === 'string' || typeof e === 'number'))
        .map((e) => e.toString().toLowerCase());

      const tvMac = simplifyMacAddress(networkInfo?.mac);
      const tvIp = networkInfo?.ip?.toLowerCase();
      const room = roomNumber?.toString().toLowerCase();

      if ((tvMac && macs.includes(tvMac)) || (tvIp && ips.includes(tvIp)) || (room && rooms.includes(room))) {
        this.logger.debug(
          '[RemoteDebugService] Remote debugger will be enabled automatically because it is enabled in "config.remoteDebug.turningOnFor"',
        );
        void this.startRemoteDebugSession();
      }
    });

    // TODO legacy
    window.remote_debug = () => void this.startRemoteDebugSession();
    window.remote_debug_on = (notifyUser?: boolean) => void this.startRemoteDebugSession(notifyUser);
    window.remote_debug_off = () => void this.stopRemoteDebugSession();
  }

  public async init(): Promise<boolean> {
    await this.checkActiveRemoteDebugSession();
    return true;
  }

  /**
   * Start or extend (if the session is already active) a remote debugging session.
   */
  public async startRemoteDebugSession(notifyUser?: boolean) {
    try {
      const sessionDuration = this.getSessionDuration();
      this.storageService.set(REMOTE_DEBUG_SESSION_END_LOCAL_STORAGE_KEY, Date.now() + sessionDuration * 60 * 1000);
      this.logger.debug(`[RemoteDebugService] Starting remote debug session for ${sessionDuration} minutes`);

      await this.enableRemoteDebug();

      if (notifyUser) {
        window.tv_log(
          '<img src="i/toasty.png" style="width:150px;"><audio id="toasty" src="tv/sound/toasty.mp3"></audio><script>$("#toasty")[0].play();</script>',
        );
      }
    } catch (err) {
      this.isRemoteDebugActive$.next(false);
      this.logger.error(`[RemoteDebugService] Unable to start remote debug session`, err);
    }
  }

  /**
   * Stop the remote debugging session.
   */
  public stopRemoteDebugSession() {
    try {
      this.storageService.remove(REMOTE_DEBUG_SESSION_END_LOCAL_STORAGE_KEY);
      if (this.isRemoteDebugActive$.getValue()) {
        this.logger.debug(`[RemoteDebugService] The remote debugging session was stopped`);
        this.isRemoteDebugActive$.next(false);
      } else {
        this.logger.debug(
          `[RemoteDebugService] Attempted to stop remote debugging session, but it was already stopped`,
        );
      }

      // cleanup debug scripts
      this.unloadChili();
      this.unloadWeinre();

      // restore logger settings
      this.restoreNormalLoggerSettings();
    } catch (err) {
      this.logger.error(`[RemoteDebugService] Unable to stop remote debug session`, err);
    }
  }

  private async checkActiveRemoteDebugSession() {
    try {
      // Check if remote debug session is still active
      const endSessionMs = this.storageService.getNumber(REMOTE_DEBUG_SESSION_END_LOCAL_STORAGE_KEY);
      if (endSessionMs && this.timeService.now.valueOf() < endSessionMs) {
        if (!this.isRemoteDebugActive$.getValue()) {
          this.logger.debug(`[RemoteDebugService] Active remote debug session detected! Enabling remote debug mode...`);
          await this.enableRemoteDebug();
        }
      } else {
        this.storageService.remove(REMOTE_DEBUG_SESSION_END_LOCAL_STORAGE_KEY);
      }
    } catch (err) {
      this.isRemoteDebugActive$.next(false);
      this.logger.error(`[RemoteDebugService] Unable to check active remote debug session`, err);
    }
  }

  private async enableRemoteDebug() {
    if (this.isRemoteDebugActive$.getValue()) {
      return;
    }
    this.isRemoteDebugActive$.next(true);

    const preferWeinre = !!this.configService.config.remoteDebug?.prefer_weinre;
    this.unloadChili();
    this.unloadWeinre();
    if (preferWeinre) {
      await this.loadWeinre();
    } else {
      await this.loadChili(); // Chili is default
    }

    this.applyDebugConsoleSettings();
  }

  private loadChili(): Promise<void> {
    return new Promise<void>((resolve) => {
      if ($('#chili_agent').length) {
        this.logger.warn(
          '[RemoteDebugService] Chili script tag (with id="chili_agent") is already loaded, no duplicate will be created.',
        );
        resolve();
        return;
      }

      let chiliUrl = this.configService.config.chili_url;
      if (!chiliUrl) {
        this.logger.error('[RemoteDebugService] Unable to start chili debug session, config.chili_url is not defined');
        resolve();
        return;
      }
      chiliUrl = `${chiliUrl}target.js`;

      $('<script></script>')
        .attr({
          'id': 'chili_agent',
          'data-scope': `Hotel ${this.hotelInfoService.hotelId} [${this.hotelInfoService.region.toUpperCase()}]`,
          'data-name': this.roomNumberService.roomNumber$.getValue() || 'N/A',
          'type': 'text/javascript',
          'src': chiliUrl,
          'onload': () => {
            this.logger.debug(`[RemoteDebugService] Chili script loaded successfully by url ${chiliUrl}`);
            resolve();
          },
          'onerror': () => {
            this.logger.error(`[RemoteDebugService] Unable to load Chili script by url ${chiliUrl}`);
            resolve();
            // error info will be handled in global error handler
            // so no error re-throw or reject() required
          },
        })
        .appendTo('head');
    });
  }

  private unloadChili() {
    $('#chili_agent').remove();
  }

  private loadWeinre(): Promise<void> {
    return new Promise<void>((resolve) => {
      if ($('#remote_debug').length) {
        this.logger.warn(
          '[RemoteDebugService] Weinre script tag (with id="remote_debug") is already loaded, no duplicate will be created.',
        );
        resolve();
        return;
      }

      let weinreUrl = this.configService.config.remotedebug_url;
      if (!weinreUrl) {
        this.logger.error(
          '[RemoteDebugService] Unable to start Weinre debug session, config.weinre_url is not defined',
        );
        resolve();
        return;
      }
      weinreUrl = `${weinreUrl}target/target-script-min.js#hotel${this.hotelInfoService.hotelId}`;

      let loaded = false;
      $('<script></script>')
        .attr({
          id: 'remote_debug',
          type: 'text/javascript',
          src: weinreUrl,
          onload: () => {
            this.logger.debug(`[RemoteDebugService] Weinre script loaded successfully by url ${weinreUrl}`);
            resolve();
            loaded = true;
          },
          onerror: () => {
            if (loaded) {
              // NOTE: For some reason Weinre can fire onerror right after onload. It seems script contains root error,
              //  but's not vital.
              return;
            }
            this.logger.error(`[RemoteDebugService] Unable to load Weinre script by url ${weinreUrl}`);
            resolve();
            // error info will be handled in global error handler
            // so no error re-throw or reject() required
          },
        })
        .appendTo('head');
    });
  }

  private unloadWeinre() {
    $('#remote_debug').remove();
  }

  private applyDebugConsoleSettings() {
    const config = this.configService.config;
    this.preservedLoggerConfig = deepClone(config.logger);
    this.configService.applyConfig({
      logger: {
        contextSize: 50,
        triggerLevel: LogLevel.Debug,
        console: {
          triggerLevel: LogLevel.Debug,
          contextSize: 0,
          objectSerialization: 'native',
          nativeLevels: true,
          colors: false,
          enabled: true,
        },
        xhr: {
          ...new XhrAppenderConfig(),
          enabled: false,
        },
        sentryBreadcrumbs: {
          ...new SentryBreadcrumbsAppenderConfig(),
          enabled: false,
        },
        localStorage: {
          ...new LocalStorageAppenderConfig(),
          triggerLevel: LogLevel.Error,
          contextSize: 20,
          size: 20,
          enabled: true,
        },
      },
    });

    this.logger.debug(`[RemoteDebugService] Applied logger settings for remote debug`);
  }

  private restoreNormalLoggerSettings() {
    if (!this.preservedLoggerConfig) {
      return;
    }
    this.configService.applyConfig({logger: this.preservedLoggerConfig});
    this.logger.debug(`[RemoteDebugService] Logger settings restored as it was before debug session`);
  }

  /**
   * Get remote debug session duration, in minutes
   */
  private getSessionDuration(): number {
    const duration = this.configService.config.remoteDebug?.debugSessionDuration;
    if (duration === 0 || duration === Infinity) {
      return Infinity;
    }
    if (typeof duration !== 'number' || duration <= 0 || !Number.isInteger(duration) || Number.isNaN(duration)) {
      return DEFAULT_DEBUG_SESSION_DURATION;
    }
    return duration;
  }

  // TODO: expose things on the global scope
  // 1. tv_up, tv_down, tv_left, tv_right, tv_keydown
  // 2. navigate
  // 3. tv_sel_cur
  // 4. tv_sel_block
  // 5. videoCollection
  // 6. tv_poweroff, tv_poweron, reload_app
  // 7. tv_mosaic.move
  // 8. Events
  // 9. CustomNotification
  // 10 _tv_channel_show, _tv_channel_stop

  // todo expose server_url global
}

declare global {
  interface Window {
    /**
     * Start remote debug session
     * @deprecated
     */
    remote_debug(): void;

    /**
     * Start remote debug session
     * @deprecated
     */
    remote_debug_on(notifyUser?: boolean): void;

    /**
     * Stop remote debug session
     * @deprecated
     */
    remote_debug_off(): void;
  }

  /**
   * Start remote debug session
   * @deprecated
   */
  const remote_debug: () => void;

  /**
   * Start remote debug session
   * @deprecated
   */
  const remote_debug_on: (notifyUser?: boolean) => void;

  /**
   * Stop remote debug session
   * @deprecated
   */
  const remote_debug_off: () => void;
}
